import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { Card, Form, Row, Col, Spinner, Container } from "react-bootstrap";
import Select from "react-select";
import Swal from "sweetalert2";
import { BASE_URL } from "../../services/Api";
import { authHeader } from "../../services/auth-header";

const Create = () => {
  const navigate = useNavigate();

  // Form state
  const [formData, setFormData] = useState({
    firstname: "",
    lastname: "",
    email: "",
    username: "",
    mobileno: "",
    password: "",
    gender: "",
  });

  const [errors, setErrors] = useState({
    email: "",
    mobileno: ""
  });

  const [touched, setTouched] = useState({
    email: false,
    mobileno: false
  });

  const [role, setRole] = useState(null);
  const [roleOptions, setRoleOptions] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Har word ka first letter capital karne ka function (Title Case)
  const capitalizeAllWords = (text) => {
    if (!text) return text;
    
    return text
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // First Name aur Last Name ke liye capital karenge
    let processedValue = value;
    if (name === 'firstname' || name === 'lastname') {
      processedValue = capitalizeAllWords(value);
    }
    
    setFormData(prev => ({ ...prev, [name]: processedValue }));

    // Validate on change if field was touched
    if (touched[name]) {
      validateField(name, processedValue);
    }
  };

  // Handle blur (when field loses focus)
  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    validateField(name, value);
  };

  // Field validation
  const validateField = (name, value) => {
    let error = "";

    switch (name) {
      case "email":
        if (!value) {
          error = "Email is required";
        } else if (!/^\S+@\S+\.\S+$/.test(value)) {
          error = "Please enter a valid email address";
        }
        break;
      case "mobileno":
        if (!value) {
          error = "Mobile number is required";
        } else if (!/^\d{10}$/.test(value)) {
          error = "Please enter a 10-digit mobile number";
        }
        break;
      default:
        break;
    }

    setErrors(prev => ({ ...prev, [name]: error }));
    return !error;
  };

  // Fetch roles on component mount
  useEffect(() => {
    const getRoleData = async () => {
      try {
        const response = await axios.get(BASE_URL + "role/findAllRole", {
          headers: authHeader(),
        });
        setRoleOptions(
          response.data.map((role) => ({
            value: role.id,
            label: role.role,
          }))
        );
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to fetch roles. Please try again.",
        });
      }
    };

    getRoleData();
  }, []);

  // Fetch departments when role is selected
  const handleRoleChange = async (selectedOption) => {
    setRole(selectedOption);
    setSelectedDepartment(null);

    try {
      const response = await axios.get(BASE_URL + "department/all", {
        headers: authHeader(),
      });

      const departmentOptions = response.data.map((dep) => ({
        value: dep.id,
        label: dep.name,
      }));

      setDepartments(departmentOptions);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to fetch departments. Please try again.",
      });
    }
  };

  // Form validation
  const validateForm = () => {
    let isValid = true;

    // Validate all required fields
    if (!formData.firstname) {
      Swal.fire({
        icon: "warning",
        title: "Incomplete Form",
        text: "First Name is required",
      });
      return false;
    }

    if (!formData.lastname) {
      Swal.fire({
        icon: "warning",
        title: "Incomplete Form",
        text: "Last Name is required",
      });
      return false;
    }

    if (!validateField("email", formData.email)) {
      isValid = false;
    }

    if (!formData.username) {
      Swal.fire({
        icon: "warning",
        title: "Incomplete Form",
        text: "Username is required",
      });
      return false;
    }

    if (!validateField("mobileno", formData.mobileno)) {
      isValid = false;
    }

    if (!formData.password) {
      Swal.fire({
        icon: "warning",
        title: "Incomplete Form",
        text: "Password is required",
      });
      return false;
    }

    if (!formData.gender) {
      Swal.fire({
        icon: "warning",
        title: "Incomplete Form",
        text: "Gender is required",
      });
      return false;
    }

    if (!role) {
      Swal.fire({
        icon: "warning",
        title: "Incomplete Form",
        text: "Role is required",
      });
      return false;
    }

    if (!selectedDepartment) {
      Swal.fire({
        icon: "warning",
        title: "Incomplete Form",
        text: "Department is required",
      });
      return false;
    }

    return isValid;
  };

  // Form submission
  const handleSubmit = async () => {
    // Mark all fields as touched to show errors
    setTouched({
      email: true,
      mobileno: true
    });

    if (!validateForm()) return;
    setIsLoading(true);

    const staffData = {
      roles: [{ id: role.value }],
      department: { id: selectedDepartment.value },
      ...formData
    };

    try {
      await axios.post(BASE_URL + "auth/registerStaff", staffData, {
        headers: authHeader(),
      });
      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Staff added successfully!",
      });
      navigate("/staffindex/1");
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error?.response?.data || "Registration failed",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container className="py-4">
      <Card className="shadow">
        <Card.Header className=" text-primary d-flex justify-content-between align-items-center">
          <h4 className="mb-0">Add New User Member</h4>
          <Link to="/staffindex/1" className="btn btn-light">
            <i className="fas fa-arrow-left me-2"></i>Back to Staff List
          </Link>
        </Card.Header>

        <Card.Body>
          <Form>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group controlId="formFirstname" className="mb-3">
                  <Form.Label>First Name <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="firstname"
                    placeholder="Enter First Name"
                    value={formData.firstname}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group controlId="formLastname" className="mb-3">
                  <Form.Label>Last Name <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="lastname"
                    placeholder="Enter Last Name"
                    value={formData.lastname}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group controlId="formEmail" className="mb-3">
                  <Form.Label>Email <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    placeholder="Enter Email"
                    value={formData.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.email && !!errors.email}
                    required
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.email}
                  </Form.Control.Feedback>
                  {!errors.email && touched.email && (
                    <Form.Text className="text-success">
                      Email looks good!
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group controlId="formUsername" className="mb-3">
                  <Form.Label>Username <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="username"
                    placeholder="Enter Username"
                    value={formData.username}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group controlId="formMobileNumber" className="mb-3">
                  <Form.Label>Mobile Number <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="tel"
                    name="mobileno"
                    placeholder="Enter Mobile Number"
                    value={formData.mobileno}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.mobileno && !!errors.mobileno}
                    required
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.mobileno}
                  </Form.Control.Feedback>
                  {!errors.mobileno && touched.mobileno && (
                    <Form.Text className="text-success">
                      Mobile number looks good!
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group controlId="formPassword" className="mb-3">
                  <Form.Label>Password <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="password"
                    name="password"
                    placeholder="Enter Password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group controlId="formGender" className="mb-3">
                  <Form.Label>Gender <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select Gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group controlId="formRole" className="mb-3">
                  <Form.Label>Role <span className="text-danger">*</span></Form.Label>
                  <Select
                    value={role}
                    onChange={handleRoleChange}
                    options={roleOptions}
                    placeholder="Select Role"
                    isClearable
                    required
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group controlId="formDepartment" className="mb-3">
                  <Form.Label>Department <span className="text-danger">*</span></Form.Label>
                  <Select
                    value={selectedDepartment}
                    onChange={setSelectedDepartment}
                    options={departments}
                    placeholder={role ? "Select Department" : "Select Role First"}
                    isDisabled={!role || departments.length === 0}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <div className="d-grid gap-2 d-md-flex justify-content-md-end">
              <button
                className="btn btn-primary px-4"
                type="button"
                onClick={handleSubmit}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                    <span className="ms-2">Processing...</span>
                  </>
                ) : (
                  <>
                    <i className="fas fa-save me-2"></i>Save User
                  </>
                )}
              </button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default Create;